export const SUPPORTED_CURRENCIES = [
  "USD",
  "SGD",
  "EUR",
  "GBP",
  "AUD",
  "CAD",
  "JPY",
  "CNY",
  "HKD",
  "KRW",
  "MYR",
  "IDR",
  "THB",
  "PHP",
  "VND",
] as const;

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

export const CURRENCY_LABELS: Record<CurrencyCode, string> = {
  USD: "USD — US Dollar",
  SGD: "SGD — Singapore Dollar",
  EUR: "EUR — Euro",
  GBP: "GBP — British Pound",
  AUD: "AUD — Australian Dollar",
  CAD: "CAD — Canadian Dollar",
  JPY: "JPY — Japanese Yen",
  CNY: "CNY — Chinese Yuan",
  HKD: "HKD — Hong Kong Dollar",
  KRW: "KRW — South Korean Won",
  MYR: "MYR — Malaysian Ringgit",
  IDR: "IDR — Indonesian Rupiah",
  THB: "THB — Thai Baht",
  PHP: "PHP — Philippine Peso",
  VND: "VND — Vietnamese Dong",
};

const EURO_COUNTRIES = new Set([
  "AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE",
  "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK",
]);

const COUNTRY_CURRENCY: Record<string, CurrencyCode> = {
  US: "USD",
  SG: "SGD",
  GB: "GBP",
  AU: "AUD",
  CA: "CAD",
  JP: "JPY",
  CN: "CNY",
  HK: "HKD",
  KR: "KRW",
  MY: "MYR",
  ID: "IDR",
  TH: "THB",
  PH: "PHP",
  VN: "VND",
};

const CURRENCY_LOCALES: Record<CurrencyCode, string> = {
  USD: "en-US",
  SGD: "en-SG",
  EUR: "de-DE",
  GBP: "en-GB",
  AUD: "en-AU",
  CAD: "en-CA",
  JPY: "ja-JP",
  CNY: "zh-CN",
  HKD: "zh-HK",
  KRW: "ko-KR",
  MYR: "ms-MY",
  IDR: "id-ID",
  THB: "th-TH",
  PHP: "en-PH",
  VND: "vi-VN",
};

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return (
    typeof value === "string" &&
    (SUPPORTED_CURRENCIES as readonly string[]).includes(value)
  );
}

export function currencyForCountry(country?: string | null): CurrencyCode {
  const code = country?.toUpperCase() ?? "";
  if (EURO_COUNTRIES.has(code)) return "EUR";
  return COUNTRY_CURRENCY[code] ?? "USD";
}

export function formatCurrency(
  amount: number,
  currency: CurrencyCode,
  precise = false,
): string {
  const formatted = new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: precise ? 4 : undefined,
    maximumFractionDigits: precise ? 4 : undefined,
  }).format(amount);
  // Several currencies share "$" or "¥". Keep the familiar local symbol but
  // append the ISO code where the symbol alone could mislead a customer.
  return ["SGD", "AUD", "CAD", "HKD", "CNY", "JPY"].includes(currency)
    ? `${formatted} ${currency}`
    : formatted;
}
